import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../hooks/useCurrency';
import { SUPPORTED_CURRENCIES } from '../../types/currency';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { 
  MaintenanceRecord, 
  MaintenanceStatus, 
  MaintenanceType,
  MaintenancePart,
  MaintenanceLabor 
} from '../../types/maintenance';
import { Timestamp } from 'firebase/firestore';

interface MaintenanceFormProps {
  maintenance?: MaintenanceRecord;
  onSubmit: (data: Partial<MaintenanceRecord>) => void;
  onCancel: () => void;
  vehicles: { id: string; name: string }[];
  serviceProviders: { id: string; name: string }[];
  preSelectedVehicleId?: string;
  disabled?: boolean;
}

const MaintenanceForm: React.FC<MaintenanceFormProps> = ({
  maintenance,
  onSubmit,
  onCancel,
  vehicles,
  serviceProviders,
  preSelectedVehicleId,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { currentUser, currentOrganization } = useAuth();
  const { format: formatCurrency, loading: currencyLoading } = useCurrency(currentOrganization?.id || '');
  const [formData, setFormData] = useState<Partial<MaintenanceRecord>>(() => {
    if (maintenance) {
      return maintenance;
    }

    const initialData = {
      status: 'pending' as MaintenanceStatus,
      type: 'revision' as MaintenanceType,
      description: '',
      costHT: 0,
      costTTC: 0,
      openDate: Timestamp.fromDate(new Date()),
      closeDate: undefined,
      events: [] as MaintenanceRecord['events'],
      documents: [] as MaintenanceRecord['documents'],
      vehicleId: preSelectedVehicleId || vehicles[0]?.id || '',
      detailedOperations: {
        parts: [],
        labor: []
      },
      organizationId: currentOrganization?.id || '',
      createdBy: currentUser?.id || '',
      updatedBy: currentUser?.id || '',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      serviceProviderId: undefined
    } satisfies Omit<MaintenanceRecord, 'id'>;

    return initialData;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.vehicleId) {
      newErrors.vehicleId = t('maintenance.form.errors.vehicleRequired');
    }

    if (!formData.type) {
      newErrors.type = t('maintenance.form.errors.typeRequired');
    }
    if (!formData.status) {
      newErrors.status = t('maintenance.form.errors.statusRequired');
    }
    if (!formData.description) {
      newErrors.description = t('maintenance.form.errors.descriptionRequired');
    }
    if (typeof formData.costHT !== 'number' || formData.costHT < 0) {
      newErrors.costHT = t('maintenance.form.errors.costHTInvalid');
    }
    if (typeof formData.costTTC !== 'number' || formData.costTTC < 0) {
      newErrors.costTTC = t('maintenance.form.errors.costTTCInvalid');
    }
    if (!formData.openDate) {
      newErrors.openDate = t('maintenance.form.errors.openDateRequired');
    }
    if (formData.status === 'completed' && !formData.closeDate) {
      newErrors.closeDate = t('maintenance.form.errors.closeDateRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateDetailedDescription = (data: Partial<MaintenanceRecord>): string => {
    const descriptions: string[] = [];

    // Add base description first
    if (data.description) {
      descriptions.push(data.description);
    }

    // Add parts details if available
    const validParts = data.detailedOperations?.parts?.filter(p => p.name) || [];
    if (validParts.length > 0) {
      descriptions.push('');  // Add empty line for spacing
      descriptions.push(t('maintenance.form.fields.parts.title') + ' :');
      validParts.forEach(part => {
        const quantity = part.quantity ? ` (${part.quantity})` : '';
        const price = part.unitPrice ? ` - ${formatCurrency(part.unitPrice)}` : '';
        descriptions.push(`- ${part.name}${quantity}${price}`);
      });
    }

    // Add labor details if available
    const validLabor = data.detailedOperations?.labor?.filter(l => l.description) || [];
    if (validLabor.length > 0) {
      descriptions.push('');  // Add empty line for spacing
      descriptions.push(t('maintenance.form.fields.labor.title') + ' :');
      validLabor.forEach(op => {
        const hours = op.hours ? ` (${op.hours}h)` : '';
        const cost = op.cost ? ` - ${formatCurrency(op.cost)}` : '';
        descriptions.push(`- ${op.description}${hours}${cost}`);
      });
    }

    return descriptions.join('\n');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Validate required fields
        if (!formData.vehicleId || !formData.organizationId || !formData.type || !formData.status) {
          throw new Error('Missing required fields');
        }

        // Prepare form data with proper typing and validation
        const submissionData: Partial<MaintenanceRecord> = {
          vehicleId: formData.vehicleId,
          organizationId: formData.organizationId,
          type: formData.type,
          status: formData.status,
          description: formData.description || '',
          costHT: typeof formData.costHT === 'number' ? formData.costHT : 0,
          costTTC: typeof formData.costTTC === 'number' ? formData.costTTC : 0,
          openDate: formData.openDate || Timestamp.now(),
          closeDate: formData.status === 'completed' 
            ? (formData.closeDate || Timestamp.now()) 
            : undefined,
          events: Array.isArray(formData.events) ? formData.events : [],
          documents: Array.isArray(formData.documents) ? formData.documents : [],
          serviceProviderId: formData.serviceProviderId,
          detailedOperations: formData.detailedOperations ? {
            parts: formData.detailedOperations.parts?.filter(p => p.name) || [],
            labor: formData.detailedOperations.labor?.filter(l => l.description) || []
          } : undefined,
          createdBy: formData.createdBy,
          updatedBy: formData.updatedBy,
          createdAt: formData.createdAt,
          updatedAt: Timestamp.now()
        };

        // Submit the validated data
        onSubmit(submissionData);
      } catch (error) {
        console.error('Form validation error:', error);
        setErrors(prev => ({
          ...prev,
          submit: t('maintenance.form.errors.validationFailed')
        }));
      }
    }
  };

  const handleChange = (field: keyof MaintenanceRecord, value: any) => {
    setFormData(prev => {
      // Create a new data object with the updated field
      const newData = { ...prev, [field]: value };

      // Handle status changes
      if (field === 'status') {
        if (value === 'completed' && !prev.closeDate) {
          // Set closeDate when status changes to completed
          newData.closeDate = Timestamp.now();
        } else if (value !== 'completed') {
          // Clear closeDate when status changes from completed
          newData.closeDate = undefined;
        }
      }

      return newData;
    });

    // Clear any errors for the changed field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDateChange = (field: 'openDate' | 'closeDate', date: Date | null) => {
    if (date) {
      handleChange(field, Timestamp.fromDate(date));
    } else {
      handleChange(field, undefined);
    }
  };

  return (
    <>
      <DialogTitle>
        {maintenance ? t('maintenance.form.editTitle') : t('maintenance.form.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.vehicleId}>
                <InputLabel>{t('maintenance.form.fields.vehicle')}</InputLabel>
                <Select
                  value={formData.vehicleId || ''}
                  onChange={(e) => handleChange('vehicleId', e.target.value)}
                  label={t('maintenance.form.fields.vehicle')}
                  disabled={!!preSelectedVehicleId || disabled}
                >
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.vehicleId && <FormHelperText>{errors.vehicleId}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.type}>
                <InputLabel>{t('maintenance.form.fields.type')}</InputLabel>
                <Select
                  value={formData.type || ''}
                  onChange={(e) => handleChange('type', e.target.value)}
                  label={t('maintenance.form.fields.type')}
                  disabled={disabled}
                >
                  <MenuItem value="revision">{t('maintenance.types.revision')}</MenuItem>
                  <MenuItem value="repair">{t('maintenance.types.repair')}</MenuItem>
                  <MenuItem value="technical_control">{t('maintenance.types.technical_control')}</MenuItem>
                </Select>
                {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>{t('maintenance.form.fields.status')}</InputLabel>
                <Select
                  value={formData.status || ''}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label={t('maintenance.form.fields.status')}
                  disabled={disabled}
                >
                  <MenuItem value="pending">{t('maintenance.status.pending')}</MenuItem>
                  <MenuItem value="in_progress">{t('maintenance.status.in_progress')}</MenuItem>
                  <MenuItem value="completed">{t('maintenance.status.completed')}</MenuItem>
                  <MenuItem value="cancelled">{t('maintenance.status.cancelled')}</MenuItem>
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Section des opérations détaillées */}
            {formData.detailedOperations && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {t('maintenance.form.fields.detailedOperations')}
                </Typography>

                {/* Tableau des pièces détachées */}
                {formData.detailedOperations.parts && formData.detailedOperations.parts.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('maintenance.form.fields.parts.title')}
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('maintenance.form.fields.parts.name')}</TableCell>
                            <TableCell align="right">{t('maintenance.form.fields.parts.quantity')}</TableCell>
                            <TableCell align="right">{t('maintenance.form.fields.parts.unitPrice')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.detailedOperations.parts.map((part: MaintenancePart, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{part.name}</TableCell>
                              <TableCell align="right">{part.quantity}</TableCell>
                              <TableCell align="right">
                                {part.unitPrice ? formatCurrency(part.unitPrice) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Tableau des opérations de main d'œuvre */}
                {formData.detailedOperations.labor && formData.detailedOperations.labor.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('maintenance.form.fields.labor.title')}
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('maintenance.form.fields.labor.description')}</TableCell>
                            <TableCell align="right">{t('maintenance.form.fields.labor.hours')}</TableCell>
                            <TableCell align="right">{t('maintenance.form.fields.labor.cost')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.detailedOperations.labor.map((operation: MaintenanceLabor, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{operation.description}</TableCell>
                              <TableCell align="right">{operation.hours}</TableCell>
                              <TableCell align="right">
                                {operation.cost ? formatCurrency(operation.cost) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('maintenance.form.fields.description')}
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('maintenance.form.fields.costHT')}
                value={formData.costHT ?? ''}
                onChange={(e) => handleChange('costHT', parseFloat(e.target.value) || 0)}
                error={!!errors.costHT}
                helperText={errors.costHT}
                InputProps={{
                  startAdornment: currencyLoading ? '' : SUPPORTED_CURRENCIES.find((c) => c.code === 'EUR')?.symbol || '€',
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('maintenance.form.fields.costTTC')}
                value={formData.costTTC ?? ''}
                onChange={(e) => handleChange('costTTC', parseFloat(e.target.value) || 0)}
                error={!!errors.costTTC}
                helperText={errors.costTTC}
                InputProps={{
                  startAdornment: currencyLoading ? '' : SUPPORTED_CURRENCIES.find((c) => c.code === 'EUR')?.symbol || '€',
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label={t('maintenance.form.fields.openDate')}
                value={formData.openDate ? formData.openDate.toDate() : null}
                onChange={(date) => handleDateChange('openDate', date)}
                disabled={disabled}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.openDate,
                    helperText: errors.openDate,
                    disabled: disabled,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label={t('maintenance.form.fields.closeDate')}
                value={formData.closeDate ? formData.closeDate.toDate() : null}
                onChange={(date) => handleDateChange('closeDate', date)}
                disabled={disabled}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.closeDate,
                    helperText: errors.closeDate,
                    disabled: disabled,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('maintenance.form.fields.serviceProvider')}</InputLabel>
                <Select
                  value={formData.serviceProviderId || ''}
                  onChange={(e) => handleChange('serviceProviderId', e.target.value)}
                  label={t('maintenance.form.fields.serviceProvider')}
                  disabled={disabled}
                >
                  {serviceProviders.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errors.submit && (
            <Box mt={2}>
              <Typography color="error" variant="body2">
                {errors.submit}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={disabled}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={disabled}
          startIcon={disabled ? <CircularProgress size={20} /> : undefined}
        >
          {maintenance ? t('common.save') : t('common.add')}
        </Button>
      </DialogActions>
    </>
  );
};

export default MaintenanceForm;
